import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { addTrailer } from '../../../store/trailers';

const TRAILER_TYPES = {
  "dry_53": "Dry 53",
  "dry_48": "Dry 48",
  "dry_42": "Dry 42",
  "dry_pup": "Dry pup",
  "reefer_53": "Reefer 53",
  "reefer_48": "Reefer 48",
  "reefer_42": "Reefer 42",
  "reefer_pup": "Reefer pup",
  "liftgate_53": "Liftgate 53",
  "liftgate_48": "Liftgate 48",
  "liftgate_42": "Liftgate 42",
  "liftgate_pup": "Liftgate pup",
  "intermodal_53": "Intermodal 53",
  "intermodal_48": "Intermodal 48",
  "intermodal_42": "Intermodal 42",
  "intermodal_pup": "Intermodal pup"
};

const statesList = [
  { name: "Alabama", abbreviation: "AL" },
  { name: "Alaska", abbreviation: "AK" },
  { name: "Arizona", abbreviation: "AZ" },
  { name: "Arkansas", abbreviation: "AR" },
  { name: "California", abbreviation: "CA" },
  { name: "Colorado", abbreviation: "CO" },
  { name: "Connecticut", abbreviation: "CT" },
  { name: "Delaware", abbreviation: "DE" },
  { name: "Florida", abbreviation: "FL" },
  { name: "Georgia", abbreviation: "GA" },
  { name: "Hawaii", abbreviation: "HI" },
  { name: "Idaho", abbreviation: "ID" },
  { name: "Illinois", abbreviation: "IL" },
  { name: "Indiana", abbreviation: "IN" },
  { name: "Iowa", abbreviation: "IA" },
  { name: "Kansas", abbreviation: "KS" },
  { name: "Kentucky", abbreviation: "KY" },
  { name: "Louisiana", abbreviation: "LA" },
  { name: "Maine", abbreviation: "ME" },
  { name: "Maryland", abbreviation: "MD" },
  { name: "Massachusetts", abbreviation: "MA" },
  { name: "Michigan", abbreviation: "MI" },
  { name: "Minnesota", abbreviation: "MN" },
  { name: "Mississippi", abbreviation: "MS" },
  { name: "Missouri", abbreviation: "MO" },
  { name: "Montana", abbreviation: "MT" },
  { name: "Nebraska", abbreviation: "NE" },
  { name: "Nevada", abbreviation: "NV" },
  { name: "New Hampshire", abbreviation: "NH" },
  { name: "New Jersey", abbreviation: "NJ" },
  { name: "New Mexico", abbreviation: "NM" },
  { name: "New York", abbreviation: "NY" },
  { name: "North Carolina", abbreviation: "NC" },
  { name: "North Dakota", abbreviation: "ND" },
  { name: "Ohio", abbreviation: "OH" },
  { name: "Oklahoma", abbreviation: "OK" },
  { name: "Oregon", abbreviation: "OR" },
  { name: "Pennsylvania", abbreviation: "PA" },
  { name: "Rhode Island", abbreviation: "RI" },
  { name: "South Carolina", abbreviation: "SC" },
  { name: "South Dakota", abbreviation: "SD" },
  { name: "Tennessee", abbreviation: "TN" },
  { name: "Texas", abbreviation: "TX" },
  { name: "Utah", abbreviation: "UT" },
  { name: "Vermont", abbreviation: "VT" },
  { name: "Virginia", abbreviation: "VA" },
  { name: "Washington", abbreviation: "WA" },
  { name: "West Virginia", abbreviation: "WV" },
  { name: "Wisconsin", abbreviation: "WI" },
  { name: "Wyoming", abbreviation: "WY" },
  { name: "District of Columbia", abbreviation: "DC" },
];

function AddTrailerPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const sessionUser = useSelector(state => state.session.user);
  const accounts = useSelector(state => Object.values(state.accounts));
  const dropdownRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const [formData, setFormData] = useState({
    account_id: sessionUser?.is_admin ? '' : sessionUser?.account_id,
    user_id: sessionUser.id,
    trailer_number: '',
    trailer_location: '',
    last_user: sessionUser.alpha_code,
    user_history: [],
    company_owner: '',
    license_plate: '',
    license_state: '',
    is_loaded: false,
    load_number: '',
    is_tray: false,
    tray_count: 0,
    yellow_tagged: false,
    red_tagged: false,
    tagged_description: '',
    type: '',
    in_shop: false,
    last_inspection: new Date().toISOString().split('T')[0],
    next_inspection: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    last_maintenance: new Date().toISOString().split('T')[0],
    next_maintenance: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [errors, setErrors] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState(statesList);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Remove event listener on cleanup
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prevIndex =>
        prevIndex < filteredStates.length - 1 ? prevIndex + 1 : prevIndex
      );
    }

    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : 0);
    }

    // Enter key
    else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleStateSelect(filteredStates[activeIndex].abbreviation);
    }

    // Escape key
    else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  // Reset active index when filtered states change
  useEffect(() => {
    setActiveIndex(-1);
  }, [filteredStates]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Define fields that should be uppercase
    const uppercaseFields = ['license_plate', 'license_state', 'trailer_location', 'trailer_number'];

    // Handle different input fields
    const processValue = {
      'default': () => uppercaseFields.includes(name) ? value.toUpperCase() : value
    };

    const newValue = (processValue[name] || processValue.default)();

    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = [];

    // Basic Information
    if (!formData.trailer_number) validationErrors.push('Trailer number is required');
    if (!formData.trailer_location) validationErrors.push('Trailer location is required');
    if (!formData.license_plate) validationErrors.push('License plate is required');
    if (!formData.license_state) validationErrors.push('License state is required');
    if (!formData.type) validationErrors.push('Trailer type is required');
    if (!formData.company_owner) validationErrors.push('Owner is required');
    if (sessionUser?.is_admin && !formData.account_id) validationErrors.push('Account is required');

    // Additional validation for account_id
    if (!formData.account_id) validationErrors.push('Account ID is required');

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const formattedData = {
        ...formData,
        account_id: parseInt(formData.account_id, 10),
        last_inspection: formData.last_inspection ? new Date(formData.last_inspection).toISOString().split('T')[0] : null,
        next_inspection: formData.next_inspection ? new Date(formData.next_inspection).toISOString().split('T')[0] : null,
        last_maintenance: formData.last_maintenance ? new Date(formData.last_maintenance).toISOString().split('T')[0] : null,
        next_maintenance: formData.next_maintenance ? new Date(formData.next_maintenance).toISOString().split('T')[0] : null,
        last_user: sessionUser.alpha_code,
        tray_count: parseInt(formData.tray_count, 10) || 0
      };

      console.log('Submitting data:', formattedData);
      const result = await dispatch(addTrailer(formattedData));

      if (result) {
        history.push('/');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors([error.toString()]);
    }
  };

  const handleStateInputChange = (e) => {
    const inputValue = e.target.value.toUpperCase();
    setFormData({ ...formData, license_state: inputValue });

    if (inputValue) {
      // Limit the number of results to prevent a huge dropdown
      const filtered = statesList
        .filter(
          (state) =>
            state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            state.abbreviation.includes(inputValue)
        )
        .slice(0, 10); // Limit to 10 results for better UX

      setFilteredStates(filtered);
      setShowDropdown(true);
    } else {
      setFilteredStates(statesList.slice(0, 10)); // Show first 10 states when input is empty
      setShowDropdown(false);
    }
  };

  const handleStateSelect = (abbreviation) => {
    setFormData({ ...formData, license_state: abbreviation });
    setShowDropdown(false);
  };

  return (
    <div className="p-5 min-h-[calc(100vh-100px)]">
      <h1 className="text-8xl font-bold mb-10 text-center text-blue-600">Add New Trailer</h1>

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 p-4 rounded-lg">
            {errors.map((error, idx) => (
              <p key={idx} className="text-red-500 text-xl">{error}</p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column - Basic Information */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Basic Information</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Trailer Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="trailer_number"
                    value={formData.trailer_number}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="trailer_location"
                    value={formData.trailer_location}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    License Plate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex flex-col relative" ref={dropdownRef}>
                  <label className="text-xl font-medium text-gray-700 mb-2">
                    License State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="license_state"
                    value={formData.license_state}
                    onChange={handleStateInputChange}
                    onKeyDown={handleKeyDown}
                    className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Type state name or abbreviation..."
                    onFocus={() => setShowDropdown(true)}
                    required
                  />

                  {showDropdown && (
                    <ul className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto z-50 bg-white shadow-lg rounded-md border border-gray-300">
                      {filteredStates.length === 0 ? (
                        <li className="p-3 text-gray-500 text-center">
                          No results found
                        </li>
                      ) : (
                        filteredStates.map((state, index) => (
                          <li
                            key={state.abbreviation}
                            onClick={() => handleStateSelect(state.abbreviation)}
                            className={`p-3 cursor-pointer text-xl border-b border-gray-200 flex justify-between ${
                              index === activeIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
                            }`}
                          >
                            <span>{state.name}</span>
                            <span className="font-bold">{state.abbreviation}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Owner <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_owner"
                    value={formData.company_owner}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {sessionUser?.is_admin && (
                  <div>
                    <label className="block text-2xl font-medium text-gray-700 mb-2">
                      Account <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="account_id"
                      value={formData.account_id}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Vehicle Details */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Trailer Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Type</option>
                    {Object.entries(TRAILER_TYPES).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-4xl font-semibold mb-6 text-gray-800">Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="red_tagged"
                  checked={formData.red_tagged}
                  onChange={handleChange}
                  className="h-6 w-6 text-blue-600"
                />
                <span className="text-2xl text-gray-700">Red Tagged</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="yellow_tagged"
                  checked={formData.yellow_tagged}
                  onChange={handleChange}
                  className="h-6 w-6 text-blue-600"
                />
                <span className="text-2xl text-gray-700">Yellow Tagged</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="in_shop"
                  checked={formData.in_shop}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.checked) {
                      setFormData((prev) => ({ ...prev, trailer_location: "Shop" }));
                    }
                  }}
                  className="h-6 w-6 text-blue-600"
                />
                <span className="text-2xl text-gray-700">In Shop</span>
              </label>
            </div>

            <div className="mt-6">
              <label className="block text-2xl font-medium text-gray-700 mb-2">
                Tagged Description
              </label>
              <textarea
                name="tagged_description"
                value={formData.tagged_description}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="4"
              />
            </div>
          </div>

          {/* Bottom Buttons */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="px-6 py-3 text-2xl text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200 w-1/4"
            >
              Cancel
            </button>
            <button
              id = 'add-trailer-button'
              type="submit"
              className="px-6 py-3 text-2xl text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 w-1/4"
            >
              Add Trailer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTrailerPage;
